import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, SxProps } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { FOCUSED_CLASS, MATCH_CLASS } from '.';

const ICON_SIZE = 20;

const buttonSx: SxProps = {
    '& svg': {
        height: ICON_SIZE,
        width: ICON_SIZE,
    },
    borderRadius: 0,
    color: 'grey.400',
    height: 24,
    px: 0.75,
};

// CSS properties order is needed this way otherwise color is overridden
const borderSx: SxProps = {
    borderLeft: 1,
    // eslint-disable-next-line sort-keys
    borderColor: 'grey.600',
};

interface ActionButtonsProps {
    inputField?: HTMLInputElement;
    onReset: () => void;
    resultCount: number;
    resultRevision: number;
    searchValue?: string;
}

const ActionButtons: FC<ActionButtonsProps> = ({
    inputField,
    onReset,
    resultCount,
    resultRevision,
    searchValue,
}) => {
    const [focusedIndex, setFocusedIndex] = useState(0);

    const changeFocusedItem = useCallback(
        (shift: number) => {
            const newIndex =
                shift === 0 || !resultCount
                    ? 0
                    : (focusedIndex + shift + resultCount) % resultCount;
            const matches = document.querySelectorAll(`.${MATCH_CLASS}`);

            if (matches.length) {
                const focusedItem = matches[newIndex];
                const oldFocus = document.querySelector(`.${FOCUSED_CLASS}`);

                oldFocus?.classList.remove(FOCUSED_CLASS);
                focusedItem.classList.add(FOCUSED_CLASS);
                focusedItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setFocusedIndex(newIndex);
        },
        [focusedIndex, resultCount],
    );

    useEffect(() => {
        const handleKeyPress = ({ key, shiftKey }: KeyboardEvent) => {
            // we want to scroll in results only if user is in search field
            if (inputField === document.activeElement && key === 'Enter')
                changeFocusedItem(shiftKey ? -1 : 1);
        };
        window.addEventListener('keypress', handleKeyPress);

        return () => window.removeEventListener('keypress', handleKeyPress);
    }, [changeFocusedItem, inputField]);

    useEffect(() => {
        changeFocusedItem(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultCount, resultRevision, searchValue]);

    if (!searchValue) return null;

    return (
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
            {resultCount > 0 && (
                <>
                    <Box
                        component="span"
                        sx={{ color: 'grey.400', fontSize: 12, pr: 0.75, whiteSpace: 'nowrap' }}
                    >
                        {focusedIndex + 1} / {resultCount}
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                        <IconButton
                            onClick={() => {
                                changeFocusedItem(1);
                            }}
                            sx={[buttonSx, borderSx]}
                        >
                            <ArrowDownwardIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                changeFocusedItem(-1);
                            }}
                            sx={buttonSx}
                        >
                            <ArrowUpwardIcon />
                        </IconButton>
                    </Box>
                </>
            )}
            <IconButton onClick={onReset} sx={[buttonSx, borderSx]}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
};

export default ActionButtons;
